import React, { createContext, useCallback, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useCreateMessageTemplateMutation } from "store/api/contractorsApi"
import { ApiTypes, UITypes } from "types"

import getErrorMessage from "utils/getErrorMessage"

interface MessageTemplateContextType {
  onCreateTemplate: (data: UITypes.MessageTemplate) => Promise<false | undefined>
  isLoading: boolean
  currentTemplateLibrary: ApiTypes.Model.TemplatesLibraryItem | null
  setCurrentTemplateLibrary: (template: ApiTypes.Model.TemplatesLibraryItem | null) => void
}

const MessageTemplateContext = createContext<MessageTemplateContextType | undefined>(undefined)

export const MessageTemplateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [triggerCreateMessageTemplate, { isLoading }] = useCreateMessageTemplateMutation()
  const [currentTemplateLibrary, setCurrentTemplateLibrary] = useState<ApiTypes.Model.TemplatesLibraryItem | null>(null)
  const navigate = useNavigate()

  const onCreateTemplate = useCallback(
    async (data: UITypes.MessageTemplate): Promise<false | undefined> => {
      try {
        const res = await triggerCreateMessageTemplate({
          title: data.title,
          content_plain: data.message,
          content_rich: data.message_rich || "",
          repeat_period: "never",
        })
        const error = getErrorMessage(res?.error)

        if (error) {
          toast.error(error)
          return false
        } else {
          toast.success("Message template has been successfully added!")
          navigate("/messages/add-template")
          setCurrentTemplateLibrary(null)
          return undefined
        }
      } catch (error) {
        console.error(error)
        return false
      }
    },
    [triggerCreateMessageTemplate, navigate],
  )

  return (
    <MessageTemplateContext.Provider
      value={{
        onCreateTemplate,
        isLoading,
        currentTemplateLibrary,
        setCurrentTemplateLibrary,
      }}
    >
      {children}
    </MessageTemplateContext.Provider>
  )
}

export const useMessageTemplateContext = () => {
  const context = useContext(MessageTemplateContext)
  if (context === undefined) {
    throw new Error("useMessageTemplateContext must be used within a MessageTemplateProvider")
  }
  return context
}
